import { useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Hook: GET /api/stats/popular → Map<bookId, views>
 */
export function usePopularStats(limit = 50) {
  const [map, setMap] = useState(new Map());
  useEffect(() => {
    let alive = true;
    axios
      .get(`/api/stats/popular?limit=${limit}`, { timeout: 4000 })
      .then(({ data }) => {
        if (!alive) return;
        const m = new Map();
        (Array.isArray(data) ? data : []).forEach((item) => m.set(String(item.bookId), item.views));
        setMap(m);
      })
      .catch(() => alive && setMap(new Map()));
    return () => { alive = false; };
  }, [limit]);
  return map;
}

/**
 * Hook: GET /api/ratings/top → Map<bookId, average>
 */
export function useTopRatedStats(limit = 50, minVotes = 1) {
  const [map, setMap] = useState(new Map());
  useEffect(() => {
    let alive = true;
    axios
      .get(`/api/ratings/top?limit=${limit}&minVotes=${minVotes}`, { timeout: 4000 })
      .then(({ data }) => {
        if (!alive) return;
        const m = new Map();
        (Array.isArray(data) ? data : []).forEach((item) => m.set(String(item.bookId), item.average));
        setMap(m);
      })
      .catch(() => alive && setMap(new Map()));
    return () => { alive = false; };
  }, [limit, minVotes]);
  return map;
}

/* ── Shared rating map ──
 * 50+ BookCards rendering at the same time would each fire their own
 * /api/ratings/top request. Cache the result module-level and notify
 * subscribed hooks once the fetch resolves. Returns
 *   Map<bookId, { average, count }>
 * — both fields so BookCard can render "★ 4.3 (12)".
 */
let _ratingMap = null;            // Map<bookId, {average, count}> once loaded
let _ratingPromise = null;        // in-flight request (avoids duplicate fetches)
const _ratingListeners = new Set();

function notifyRatingListeners() {
  const snapshot = _ratingMap || new Map();
  _ratingListeners.forEach((fn) => fn(snapshot));
}

function startRatingFetch() {
  if (_ratingPromise) return _ratingPromise;
  _ratingPromise = axios
    .get('/api/ratings/top?limit=500&minVotes=1', { timeout: 6000 })
    .then(({ data }) => {
      const m = new Map();
      (Array.isArray(data) ? data : []).forEach((item) => {
        m.set(String(item.bookId), { average: item.average, count: item.count });
      });
      _ratingMap = m;
      notifyRatingListeners();
      return m;
    })
    .catch(() => {
      _ratingMap = new Map();
      notifyRatingListeners();
      return _ratingMap;
    });
  return _ratingPromise;
}

export function useRatingMap() {
  const [map, setMap] = useState(_ratingMap || new Map());
  useEffect(() => {
    _ratingListeners.add(setMap);
    if (_ratingMap) {
      // Late mounters get the cached snapshot immediately.
      setMap(_ratingMap);
    } else {
      startRatingFetch();
    }
    return () => { _ratingListeners.delete(setMap); };
  }, []);
  return map;
}

/** Invalidate the cached map — call after a rating is submitted. */
export function invalidateRatingMap() {
  _ratingMap = null;
  _ratingPromise = null;
  startRatingFetch();
}

/**
 * Order a book list by an external ranking map (popular views or top-rated avg).
 * Books not present in the ranking are dropped. Falls back to `fallback` when
 * the ranking map is empty (e.g., cold start, no data yet).
 */
export function applyRanking(books, rankingMap, fallback = books) {
  if (!rankingMap || rankingMap.size === 0) return fallback;
  const ranked = books
    .map((b) => ({ b, score: rankingMap.get(String(b._id)) }))
    .filter((x) => x.score != null)
    .sort((a, b) => b.score - a.score)
    .map((x) => x.b);
  return ranked.length ? ranked : fallback;
}

/**
 * Fire-and-forget visit recorder. Safe to call without auth (no-op when no token).
 */
export async function recordVisit(bookId) {
  if (!bookId) return;
  const token = typeof window !== 'undefined' ? localStorage.getItem('adeeb-token') : null;
  if (!token) return;
  try {
    await axios.post(
      '/api/stats/visit',
      { bookId: String(bookId) },
      { headers: { Authorization: `Bearer ${token}` }, timeout: 4000 }
    );
  } catch {
    /* swallow — analytics shouldn't disrupt the read */
  }
}
